<template>
  <!-- Mobile Card Layout (sm and below) -->
  <div
    class="sm:hidden bg-white border rounded-lg shadow-sm p-3 transition-shadow duration-200 hover:shadow-md"
    :class="{
      'mb-0 py-0': compact,
      'mb-3': !compact,
      'border-primary': member.role === 'manager',
      'border-gray': member.role !== 'manager'
    }"
  >
    <!-- Card Header -->
    <div class="tw-flex items-center space-x-3 mb-3">
      <div
        @click="startConversation(null, member)"
        v-tooltip.top="disableConversation ? '' : 'Start conversation with ' + member.name"
        class="w-12 h-12 rounded-full border border-gray-300 bg-no-repeat bg-cover bg-center flex-shrink-0 mt-2"
        :class="{ 'cursor-pointer': !disableConversation }"
        :style="{ backgroundImage: `url(${member.photoUrl})` }"
      />
      <div class="min-w-0 flex-1">
        <div class="font-medium text-sm text-gray-900 truncate">{{ member.name }}</div>
        <span v-if="member.role === 'manager'" class="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap bg-primary text-white mt-1 self-start">Community Manager</span>
        <a
          v-if="showEmail"
          class="text-xs text-gray-600 hover:text-primary no-underline truncate block"
          :href="`mailto:${member.email}`"
        >{{ member.email }}</a>
      </div>
    </div>

    <!-- Card Details -->
    <div class="space-y-2 mb-3" v-if="showOrganization || showDepartment || showExperience">
      <div v-if="showOrganization" class="tw-flex justify-between items-start">
        <span class="text-xs font-medium text-gray-500 flex-shrink-0">Organization:</span>
        <div class="text-xs text-gray-900 truncate ml-2 text-right">
          <div v-if="showOrganizationLink && member.latestClaim && member.latestClaim.name && member.latestClaim.elastic_id"
               class="no-underline hover:underline cursor-pointer text-primary"
               @click="goToActorDetail">{{
              member.latestClaim && member.latestClaim.name ? member.latestClaim.name : '-'
            }}
          </div>
          <span v-else>{{ member.latestClaim && member.latestClaim.name ? member.latestClaim.name : '-' }}</span>
        </div>
      </div>

      <div v-if="showDepartment" class="tw-flex justify-between items-start">
        <span class="text-xs font-medium text-gray-500 flex-shrink-0">Department:</span>
        <span class="text-xs text-gray-900 truncate ml-2 text-right">
          {{ member.department.length > 0 ? member.department : '-' }}
        </span>
      </div>

      <div v-if="showExperience" class="tw-flex justify-between items-start">
        <span class="text-xs font-medium text-gray-500 flex-shrink-0">Experience:</span>
        <span class="text-xs text-gray-900 truncate ml-2 text-right">
          {{ member.function.length > 0 ? member.function : '-' }}
        </span>
      </div>
    </div>

    <!-- Card Actions -->
    <div class="tw-flex space-x-2" v-if="showActions && member.id !== loggedInUserId">
      <ds-button
        size="extra-small"
        variant="rounded"
        label="Chat"
        @click="startConversation($event, member)"
        class="flex-1 text-xs"
      />
      <ds-button
        size="extra-small"
        variant="rounded-secondary"
        label="View"
        @click="openUserSidePanel(member)"
        class="flex-1 text-xs"
      />
    </div>
  </div>

  <!-- Desktop Horizontal Layout (sm and above) -->
  <div
    class="hidden sm:tw-flex bg-gray-50 rounded overflow-x-auto items-center"
    :class="{
      'mb-0 !py-[3px]': compact,
      'mb-4 md:mb-5': !compact,
      'px-3 py-2 md:px-5 md:py-3': true,
      'border border-primary': member.role === 'manager'
    }"
  >
    <!-- Avatar -->
    <div class="tw-flex items-center flex-shrink-0">
      <div
        @click="startConversation(null, member)"
        v-tooltip.top="disableConversation ? '' : this.$t('start_conversation') + ' ' + member.name"
        class="rounded-full border border-black bg-no-repeat bg-contain bg-center"
        :class="{
          'w-6 h-6': compact,
          'w-7 h-7 md:w-8 md:h-8': !compact,
          'cursor-pointer': !disableConversation
        }"
        :style="{
          backgroundImage: `url(${member.photoUrl})`,
          margin: compact ? '6px' : '0 8px 0 0'
        }"
      />
    </div>

    <!-- Content sections - responsive flex layout -->
    <div class="tw-flex flex-1 overflow-hidden">
      <!-- Name & Email -->
      <div class="tw-flex flex-col justify-center flex-shrink-0 mt-2"
           :class="{
             'px-1 min-w-[120px] max-w-[120px] md:min-w-[180px] md:max-w-[180px]': true,
             'text-xs': compact,
             'text-xs md:text-sm': !compact
           }">
        <div class="font-medium truncate">{{ member.name }}</div>
        <a
          v-if="showEmail"
          class="no-underline text-ellipsis whitespace-nowrap overflow-hidden hover:underline"
          :href="`mailto:${member.email}`"
        >{{ member.email }}</a>
      </div>

      <!-- Organization -->
      <div
        v-if="showOrganization"
        class="tw-flex flex-col justify-center flex-shrink-0"
        :class="{
          'px-1 min-w-[120px] max-w-[120px] md:min-w-[180px] md:max-w-[180px]': true,
          'text-xs': compact,
          'text-xs md:text-sm': !compact
        }"
      >
        <div v-if="showOrganizationTitle" class="font-medium text-xs">{{ $t('add_actor_legal_entity_label') }}</div>
        <div class="no-underline text-ellipsis whitespace-nowrap overflow-hidden">
          <div v-if="showOrganizationLink && member.latestClaim && member.latestClaim.name && member.latestClaim.elastic_id"
               class="no-underline hover:underline cursor-pointer text-primary"
               @click="goToActorDetail">{{
              member.latestClaim && member.latestClaim.name ? member.latestClaim.name : '-'
            }}
          </div>
          <span v-else>{{ member.latestClaim && member.latestClaim.name ? member.latestClaim.name : '-' }}</span>
        </div>
      </div>

      <!-- Department -->
      <div
        v-if="showDepartment"
        class="tw-flex !flex-col justify-center flex-shrink-0 hidden xl:!block"
        :class="{
          'px-1 min-w-[120px] max-w-[120px] lg:min-w-[180px] lg:max-w-[180px]': true,
          'text-xs': compact,
          'text-xs md:text-sm': !compact
        }"
      >
        <div class="font-medium text-xs">{{ departmentLabel }}</div>
        <div class="no-underline text-ellipsis whitespace-nowrap overflow-hidden">
          {{ departmentName ? departmentName : '-' }}
        </div>
      </div>

      <!-- Experience -->
      <div
        v-if="showExperience"
        class="tw-flex !flex-col justify-center flex-shrink-0 hidden xl:!block"
        :class="{
          'px-1 min-w-[120px] max-w-[120px] xl:min-w-[180px] xl:max-w-[180px]': true,
          'text-xs': compact,
          'text-xs md:text-sm': !compact
        }"
      >
        <div class="font-medium text-xs">{{ experienceLevelLabel }}</div>
        <div class="no-underline text-ellipsis whitespace-nowrap overflow-hidden">{{
            experienceLevelName ? experienceLevelName : '-'
          }}
        </div>
      </div>

      <div>
        <span
          v-if="member.role === 'manager' && !compact"
          class="flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap bg-primary text-white mx-3"
        >Community Manager</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="ml-auto tw-flex flex-shrink-0" v-if="showActions && member.id !== loggedInUserId">
      <div class="tw-flex flex-row gap-2">
        <ds-button
          size="extra-small"
          variant="rounded"
          :label="$t('conversation_start')"
          @click="startConversation($event, member)"
          class="text-xs whitespace-nowrap"
        />

        <ds-button
          v-if="member.id !== loggedInUserId"
          size="extra-small"
          variant="rounded-secondary"
          :label="$t('knowledge_base_resource_actions_announcement_VIEW')"
          @click="openUserSidePanel(member)"
          class="text-xs whitespace-nowrap"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
  import { defineComponent } from 'vue'
  import CommunityMixin from '@/util/CommunityMixin'
  import { Conversations } from '@/api/conversations'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'

  export default defineComponent({
    name: 'CommunityMemberCard',
    mixins: [CommunityMixin, TaxonomyMixin, TranslationsMixin],
    props: {
      member: {
        type: Object,
        required: true,
      },
      showActions: {
        type: Boolean,
        default: true
      },
      showExperience: {
        type: Boolean,
        default: true,
      },
      showDepartment: {
        type: Boolean,
        default: true,
      },
      showOrganization: {
        type: Boolean,
        default: true,
      },
      showOrganizationTitle: {
        type: Boolean,
        default: true,
      },
      showOrganizationLink: {
        type: Boolean,
        default: false,
      },
      compact: {
        type: Boolean,
        default: false,
      },
      showEmail: {
        type: Boolean,
        default: false,
      },
      disableConversation: {
        type: Boolean,
        default: false,
      }
    },
    computed: {
      latestClaimUrl() {
        if (!this.communityId) {
          return `/actors/${this.member.latestClaim.elastic_id}`
        }

        return `/communities/${this.communityId}/actors/${this.member.latestClaim.elastic_id}`
      },
      loggedInUserId() {
        return this.$store.getters.userId
      },
      departmentName() {
        if (!this.member.department) return ''

        const department = this.$store.state.taxonomies.departments.find(d => d.id === Number(this.member.department))
        if (!department) return this.member.department

        return this.getTranslatedTaxonomyName(department)
      },
      // Get translated experience level name based on function ID
      experienceLevelName() {
        if (!this.member.function) return ''
        const experienceLevel = this.$store.state.taxonomies.experienceLevels.find(e => e.id === Number(this.member.function))
        if (!experienceLevel) return this.member.function

        return this.getTranslatedTaxonomyName(experienceLevel)
      },
    },
    methods: {
      goToActorDetail() {
        return this.$router.push(this.latestClaimUrl)
      },
      startConversation($event, member) {
        if (this.disableConversation) {
          return
        }
        Conversations
          .post({ recipient_id: member.id })
          .then((conversation) => {
            this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
            this.$router.push('/profile/conversations/' + conversation.id)
              .then(() => {
                this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
                  component: 'user-information-side-panel',
                  metaData: {
                    userId: member.id,
                    allowDelete: false,
                  },
                })
              })
          })
      },
      openUserSidePanel(member) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'user-information-side-panel',
          metaData: {
            userId: member.id,
            allowDelete: false,
          },
        })
      },

    }
  })
</script>
<style scoped>
  /* Custom color variables that reference CSS custom properties */
  .bg-primary {
    background-color: var(--primary-community, #3b82f6);
  }

  .bg-primary-extra-lightest {
    background-color: var(--primary-community-extra-lightest, #eff6ff);
  }

  .bg-primary-lighter {
    background-color: var(--primary-community-lighter, #dbeafe);
  }

  .text-primary {
    color: var(--primary-community, #3b82f6);
  }

  .border-primary {
    border-color: var(--primary-community, #3b82f6);
  }

  .border-primary-lighter {
    border-color: var(--primary-community-lighter, #dbeafe);
  }

  .accent-primary {
    accent-color: var(--primary-community, #3b82f6);
  }

  .ring-primary\/10 {
    --tw-ring-color: rgba(var(--primary-community-rgb, 59, 130, 246), 0.1);
  }

  .hover\:bg-primary-lighter:hover {
    background-color: var(--primary-community-lighter, #dbeafe);
  }

  .hover\:border-primary-lighter:hover {
    border-color: var(--primary-community-lighter, #dbeafe);
  }

  .hover\:text-primary:hover {
    color: var(--primary-community, #3b82f6);
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .filter-dropdown {
      position: fixed !important;
      left: 0.5rem !important;
      right: 0.5rem !important;
      min-width: auto !important;
      max-width: none !important;
    }
  }
</style>
